import { X, Download, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Full-screen modal PDF viewer using browser's native PDF renderer via iframe.
 * Props:
 *   fileUrl - URL to /library/files/{id}/view (inline content-disposition)
 *   downloadUrl - URL to /library/files/{id}/download
 *   title - file name
 *   onClose - close handler
 */
export const PdfViewerModal = ({ fileUrl, downloadUrl, title, onClose, lang = "ms" }) => {
  return (
    <AnimatePresence>
      {fileUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          onClick={onClose}
          data-testid="pdf-viewer-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="bg-white rounded-[20px] w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center gap-3 px-4 py-3 border-b border-[rgba(0,0,0,0.06)] flex-shrink-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-[#2563EB] uppercase tracking-wider">
                  {lang === "en" ? "Preview" : "Prebiu"}
                </p>
                <h3 className="text-sm font-bold text-[#0A0A0A] truncate" title={title}>
                  {title}
                </h3>
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#0A0A0A] hover:bg-[#F7F7F7] transition-colors"
                  data-testid="pdf-viewer-download"
                >
                  <Download size={14} /> {lang === "en" ? "Download" : "Muat Turun"}
                </a>
              )}
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#0A0A0A] hover:bg-[#F7F7F7] transition-colors"
                data-testid="pdf-viewer-open"
              >
                <ExternalLink size={14} /> {lang === "en" ? "Open" : "Buka"}
              </a>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-[#F7F7F7] flex items-center justify-center transition-colors"
                data-testid="pdf-viewer-close"
              >
                <X size={18} className="text-[#0A0A0A]" />
              </button>
            </header>
            <div className="flex-1 bg-[#1a1a1a]">
              <iframe
                src={fileUrl}
                title={title}
                className="w-full h-full"
                style={{ border: 0 }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
